import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, ToastContainer, ToastVariant, ToastPosition } from '@/components/ui/Toast';

interface ToastItem {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string | ReactNode;
  duration?: number;
}

interface ToastContextValue {
  toast: {
    success: (message: string, title?: string) => void;
    error: (message: string, title?: string) => void;
    warning: (message: string, title?: string) => void;
    info: (message: string, title?: string) => void;
  };
  showToast: (options: Omit<ToastItem, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export interface ToastProviderProps {
  children: ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
}

/**
 * ToastProvider - Proveedor de contexto para toasts
 * 
 * Agregar en App.tsx:
 * ```tsx
 * import { ToastProvider } from '@/hooks/useToast';
 * 
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <Router />
 *     </ToastProvider>
 *   );
 * }
 * ```
 */
export function ToastProvider({ 
  children, 
  position = 'top-right',
  maxToasts = 5 
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((options: Omit<ToastItem, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    setToasts((prev) => {
      const newToasts = [...prev, { ...options, id }];
      // Limitar cantidad de toasts
      if (newToasts.length > maxToasts) {
        return newToasts.slice(-maxToasts);
      }
      return newToasts;
    });
  }, [maxToasts]);

  const toast = {
    success: (message: string, title?: string) => 
      showToast({ variant: 'success', message, title }),
    error: (message: string, title?: string) => 
      showToast({ variant: 'error', message, title }),
    warning: (message: string, title?: string) => 
      showToast({ variant: 'warning', message, title }),
    info: (message: string, title?: string) => 
      showToast({ variant: 'info', message, title }),
  };

  return (
    <ToastContext.Provider value={{ toast, showToast }}>
      {children}
      <ToastContainer position={position}>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            id={t.id}
            variant={t.variant}
            title={t.title}
            message={t.message}
            duration={t.duration}
            onClose={removeToast}
          />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

/**
 * useToast - Hook para mostrar toasts
 * 
 * Uso:
 * ```tsx
 * import { useToast } from '@/hooks/useToast';
 * 
 * function MyComponent() {
 *   const { toast } = useToast();
 * 
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       toast.success('Guardado correctamente');
 *     } catch (error) {
 *       toast.error('Error al guardar');
 *     }
 *   };
 * }
 * ```
 */
export function useToast() {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
}









